(() => {
  'use strict';

  if (window.PWProductWidePurchaseLoaded) return;
  window.PWProductWidePurchaseLoaded = true;

  const normalize = (value) => String(value || '').trim().toLowerCase();

  const make = (tag, className = '', text = '') => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };

  const ensureSummaryPrice = (buybox, summary) => {
    const source = buybox.querySelector(':scope > .pw-product-price');
    let target = summary.querySelector(':scope > .pw-product-hero__summary-price');

    if (!source) {
      if (target) target.remove();
      return null;
    }

    if (!target) {
      target = make('div', 'pw-product-price pw-product-hero__summary-price');
      target.setAttribute('aria-live', 'polite');
      target.setAttribute('aria-label', 'Productprijs');
    }

    const sync = () => {
      const current = source.querySelector('[data-pw-price-current]');
      const compare = source.querySelector('[data-pw-price-compare]');
      const isRequest = source.classList.contains('pw-product-price--request');

      target.classList.toggle('pw-product-hero__summary-price--request', isRequest);
      target.replaceChildren();

      if (current) {
        target.append(make(
          'span',
          'pw-product-hero__summary-price-current',
          current.textContent.trim()
        ));

        if (compare && !compare.hidden && compare.textContent.trim()) {
          target.append(make(
            's',
            'pw-product-hero__summary-price-compare',
            compare.textContent.trim()
          ));
        }
      } else {
        target.textContent = source.textContent.trim();
      }
    };

    sync();

    if (target.dataset.pwPriceObserverBound !== 'true') {
      target.dataset.pwPriceObserverBound = 'true';
      new MutationObserver(sync).observe(source, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['hidden', 'class']
      });
    }

    const header = summary.querySelector(':scope > .pw-product-hero__header');
    if (header) header.insertAdjacentElement('afterend', target);
    else summary.prepend(target);

    return target;
  };

  const updateExtraOptionsVisibility = (buybox) => {
    const details = buybox.querySelector('details.pw-buybox-options');
    if (!details) return;

    const serviceSections = Array.from(details.querySelectorAll('.pw-services'));
    serviceSections.forEach((section) => {
      const hasServices = Boolean(
        section.querySelector('.pw-service-input[data-pw-service-variant]')
      );
      section.hidden = !hasServices;
      section.style.display = hasServices ? '' : 'none';
      section.setAttribute('aria-hidden', hasServices ? 'false' : 'true');
    });

    const hasAnyServices = serviceSections.some((section) => !section.hidden);
    details.hidden = !hasAnyServices;
    details.style.display = hasAnyServices ? '' : 'none';
    details.setAttribute('aria-hidden', hasAnyServices ? 'false' : 'true');

    if (details.dataset.pwEmptyOptionsObserverBound !== 'true') {
      details.dataset.pwEmptyOptionsObserverBound = 'true';
      new MutationObserver(() => updateExtraOptionsVisibility(buybox)).observe(details, {
        childList: true,
        subtree: true
      });
    }
  };

  const ensureTestlabReasons = (page, summary) => {
    const plusGroup = Array.from(page.querySelectorAll('.pw-testlab__group--positive')).find((group) => {
      const heading = group.querySelector('h3');
      return normalize(heading && heading.textContent) === 'pluspunten';
    });

    const sourceItems = plusGroup
      ? Array.from(plusGroup.querySelectorAll('.pw-list > li')).slice(0, 3)
      : [];

    let block = summary.querySelector(':scope > .pw-product-hero__testlab');
    if (!sourceItems.length) {
      if (block) block.remove();
      return null;
    }

    if (!block) {
      block = make('section', 'pw-product-hero__testlab');
      block.setAttribute('aria-label', 'Sterke punten uit het Paulissen Testlab');
    }
    block.replaceChildren();

    const header = make('div', 'pw-product-hero__testlab-header');
    const copy = make('div', 'pw-product-hero__testlab-copy');
    copy.append(make('p', 'pw-eyebrow', 'Paulissen Testlab'), make('h2', '', 'Sterke punten'));
    header.append(copy);

    const score = page.querySelector('.pw-testlab__score-value');
    const verdict = page.querySelector('.pw-testlab__verdict');
    const badgeText = [score && score.textContent.trim(), verdict && verdict.textContent.trim()].filter(Boolean).join(' · ');
    if (badgeText) header.append(make('span', 'pw-product-hero__testlab-badge', badgeText));

    const list = make('ul', 'pw-product-hero__testlab-list');
    sourceItems.forEach((item) => list.append(item.cloneNode(true)));
    block.append(header, list);
    return block;
  };

  const ensureCriticalSpecs = (page, summary) => {
    let highlights = summary.querySelector(':scope > .pw-highlights') || page.querySelector('.pw-page-shell > .pw-highlights');
    const specs = page.querySelector('.pw-specifications');

    if (!highlights && !specs) return null;

    if (!highlights) {
      highlights = make('section', 'pw-section pw-highlights pw-highlights--hero-generated');
      highlights.dataset.pwModule = 'highlights';
      const header = make('div', 'pw-section__header');
      header.append(make('p', 'pw-eyebrow', 'In één oogopslag'), make('h2', '', 'Belangrijkste specificaties'));
      highlights.append(header, make('div', 'pw-stat-grid'));
    }

    const eyebrow = highlights.querySelector('.pw-eyebrow');
    const title = highlights.querySelector('.pw-section__header h2');
    if (eyebrow) eyebrow.textContent = 'In één oogopslag';
    if (title) title.textContent = 'Belangrijkste specificaties';

    const grid = highlights.querySelector('.pw-stat-grid');
    if (!grid || !specs) return highlights;

    const existing = new Set(Array.from(grid.querySelectorAll('.pw-stat__label')).map((node) => normalize(node.textContent)));
    const rows = new Map();
    specs.querySelectorAll('.pw-spec').forEach((row) => {
      const label = row.querySelector('dt');
      const value = row.querySelector('dd');
      if (!label || !value) return;
      rows.set(normalize(label.textContent), { label: label.textContent.trim(), value });
    });

    const addCard = (label, valueNode, key = label) => {
      if (grid.children.length >= 6 || existing.has(normalize(key))) return false;
      const card = make('div', 'pw-stat pw-stat--critical');
      card.append(make('span', 'pw-stat__label', label));
      const value = make('strong', 'pw-stat__value');
      if (valueNode instanceof Element) {
        Array.from(valueNode.childNodes).forEach((node) => value.append(node.cloneNode(true)));
      } else {
        value.textContent = String(valueNode || '');
      }
      card.append(value);
      grid.append(card);
      existing.add(normalize(key));
      return true;
    };

    const addRow = (label) => {
      const row = rows.get(normalize(label));
      if (!row) return false;
      return addCard(row.label, row.value, row.label);
    };

    addRow('Energielabel');
    addRow('Techniek');

    const height = rows.get('hoogte');
    const width = rows.get('breedte');
    const depth = rows.get('diepte');
    if (height && width && depth && grid.children.length < 6 && !existing.has('afmetingen')) {
      const dimensions = [height.value.textContent.trim(), width.value.textContent.trim(), depth.value.textContent.trim()].join(' × ');
      addCard('Afmetingen (h × b × d)', dimensions, 'Afmetingen');
    }

    [
      'Vulgewicht',
      'Capaciteit',
      'Geluidsniveau',
      'Toerental',
      'Elektrische aansluiting',
      'Aantal kookzones',
      'Maximale afzuigcapaciteit',
      'Inhoud koelgedeelte',
      'Inhoud vriesgedeelte',
      'Nismaat',
      'Deurtype',
      'Plaatsing'
    ].forEach(addRow);

    return highlights;
  };

  const enhancePage = (page) => {
    if (!(page instanceof Element)) return;

    const grid = page.querySelector('.pw-product-hero__grid');
    const media = grid && grid.querySelector(':scope > .pw-product-hero__media');
    const buybox = grid && grid.querySelector(':scope > .pw-product-hero__buybox');
    if (!grid || !media || !buybox) return;

    let summary = grid.querySelector(':scope > .pw-product-hero__summary');
    if (!summary) {
      summary = document.createElement('aside');
      summary.className = 'pw-product-hero__summary';
      summary.setAttribute('aria-label', 'Productinformatie');
      grid.insertBefore(summary, buybox);
    }

    const header = buybox.querySelector(':scope > .pw-product-hero__header');
    if (header && header.parentElement !== summary) summary.prepend(header);

    ensureSummaryPrice(buybox, summary);
    updateExtraOptionsVisibility(buybox);

    const testlab = ensureTestlabReasons(page, summary);
    const highlights = ensureCriticalSpecs(page, summary);
    if (testlab) summary.append(testlab);
    if (highlights) summary.append(highlights);

    grid.classList.add('pw-product-hero__grid--wide-purchase');
    buybox.classList.add('pw-product-hero__buybox--wide');
  };

  const init = (root = document) => {
    if (root instanceof Element && root.matches('.pw-product-page')) enhancePage(root);
    root.querySelectorAll('.pw-product-page').forEach(enhancePage);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('.pw-product-page') || node.querySelector('.pw-product-page')) {
          init(document);
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
